import { Injectable, computed, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { API_BASE_URL } from './api-config';
import { LoginRequest, LoginResponse, UserDto } from '../models/auth.models';

const STORAGE_KEY = 'fms.auth';

interface StoredAuth {
  accessToken: string;
  expiresAt: string;
  user: UserDto;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly state = signal<StoredAuth | null>(this.readStorage());

  readonly currentUser = computed(() => this.state()?.user ?? null);
  readonly isAuthenticated = computed(() => {
    const s = this.state();
    return !!s && new Date(s.expiresAt).getTime() > Date.now();
  });
  readonly roles = computed(() => this.state()?.user.roles ?? []);

  constructor(private http: HttpClient) {}

  get accessToken(): string | null {
    return this.state()?.accessToken ?? null;
  }

  hasRole(role: string): boolean {
    return this.roles().includes(role);
  }

  login(request: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${API_BASE_URL}/auth/login`, request).pipe(
      tap((response) => {
        const stored: StoredAuth = { accessToken: response.accessToken, expiresAt: response.expiresAt, user: response.user };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
        this.state.set(stored);
      })
    );
  }

  logout(): void {
    localStorage.removeItem(STORAGE_KEY);
    this.state.set(null);
  }

  private readStorage(): StoredAuth | null {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as StoredAuth;
    } catch {
      return null;
    }
  }
}
